import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormikContext } from 'formik'

import { calculatePercentageDiscountedPrice } from 'core'
import getDiscountCodeInfo, { DiscountInfo } from 'core/application/account/discountCodeInfo'
import SubscriptionFrequency from 'core/application/adminSignUp/common/subscriptionFrequency'
import { ProductIds } from 'core/application/commons/config'
import getSubscriptionFees, { SubscriptionFeesResponse } from 'core/application/subscriptions/getSubscriptionFees'
import getSubscriptionPlansForPackage, {
  SubscriptionPlansForPackageResponse,
} from 'core/application/subscriptions/getSubscriptionPlansForPackage'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import { Col, DiscountAlert, Radio, Row, Skeleton } from 'common/components'
import { useQueryRequest, useQueryRequestV2 } from 'common/hooks'
import { formatMoney } from 'common/moneyFormatter'

type SubscriptionFeesProps = {
  selectedSubscriptionPlan: SubscriptionPlansForPackageResponse
  subscriptionPackageCode: string
  discountCode?: string
  discountCodeYearly?: string
  onSubscriptionPlanChange?: (arg: SubscriptionPlansForPackageResponse) => void
  onDiscountCodeChange?: (value: string) => void
}

const SubscriptionTypes = ({
  selectedSubscriptionPlan,
  discountCode,
  discountCodeYearly,
  subscriptionPackageCode,
  onSubscriptionPlanChange,
  onDiscountCodeChange,
}: SubscriptionFeesProps) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const monthNumber = 12
  const [subscriptionPlanFees] = useQueryRequest<SubscriptionPlansForPackageResponse[]>(null, () =>
    getSubscriptionPlansForPackage(subscriptionPackageCode),
  )
  const { values } = useFormikContext<{ seatNumber: number }>()
  const { seatNumber } = values

  const currentSeatNumber =
    seatNumber < selectedSubscriptionPlan?.minimumSeats ? selectedSubscriptionPlan?.minimumSeats : seatNumber

  const [fees, isLoadingFees, sendGetPerksSubscriptionFeesRequest] = useQueryRequest<SubscriptionFeesResponse>(null)

  const { sendQuery: sendGetDiscountInfo } = useQueryRequestV2<DiscountInfo>()
  const [discount, setDiscount] = useState<DiscountInfo | null>(null)

  useEffect(() => {
    if (subscriptionPlanFees && subscriptionPlanFees.length > 0) {
      if (subscriptionPlanFees.length == 1) {
        onSubscriptionPlanChange(subscriptionPlanFees[0])
      } else {
        onSubscriptionPlanChange(subscriptionPlanFees[1])
      }
    }
  }, [subscriptionPlanFees])

  useEffect(() => {
    ;(async () => {
      if (selectedSubscriptionPlan) {
        await sendGetPerksSubscriptionFeesRequest(() =>
          getSubscriptionFees(selectedSubscriptionPlan.code, selectedSubscriptionPlan.productId, currentSeatNumber),
        )

        const resolvedDiscountCode =
          selectedSubscriptionPlan.frequency.id === SubscriptionFrequency.Monthly ? discountCode : discountCodeYearly

        if (resolvedDiscountCode) {
          const result = await sendGetDiscountInfo(() => getDiscountCodeInfo(resolvedDiscountCode))
          setDiscount(result)
          onDiscountCodeChange(resolvedDiscountCode)
        } else {
          setDiscount(null)
          onDiscountCodeChange(null)
        }
      }
    })()
  }, [selectedSubscriptionPlan, currentSeatNumber])

  return (
    <>
      <Row justify="center" className="mt-8 mb-8">
        {selectedSubscriptionPlan != null && (
          <Col xs={24}>
            <Radio.Group
              name="code"
              defaultValue={selectedSubscriptionPlan.code}
              onRadioChange={(e) => {
                const found = subscriptionPlanFees.find((x) => x.code == e.target.value)
                onSubscriptionPlanChange(found)
              }}
            >
              <Row gutter={10}>
                {subscriptionPlanFees?.map((item, index) => (
                  <Col key={`${index}`} xs={12}>
                    {item.frequency.id === SubscriptionFrequency.Annual ? (
                      <Radio.Button className="white-large" value={item.code}>
                        <div className="pb-16">
                          <p className="radio-button-title">
                            {translate('admin-signup.subscription.subscription-fees.annual')}
                          </p>
                          <ul className="pt-16">
                            <li>
                              <p className="mb-2 flex">
                                <div className="mr-4">Setup Fee</div>
                                {item.saveOnSetupFee > 0 && (
                                  <div className={'line-through text-decoration-red mr-4'}>
                                    <b> ${item.saveOnSetupFee} </b>
                                  </div>
                                )}

                                <div>
                                  <b>${item.setupFee}</b>
                                </div>
                              </p>
                              {item.saveOnSetupFee > 0 && (
                                <small className="primary-color mb-4">Save ${item.saveOnSetupFee}</small>
                              )}
                            </li>
                            <li>
                              <p className="mb-2 flex">
                                <div className="mr-4">Platform Fee</div>
                                {item.saveOnMonthlyFee && (
                                  <div className={'line-through text-decoration-red mr-4'}>
                                    <b> ${item.saveOnMonthlyFee / monthNumber}</b>/ mo
                                  </div>
                                )}
                                <div>
                                  <b> ${item.monthlyFee / monthNumber}</b>/ mo
                                </div>
                              </p>
                              {item.saveOnMonthlyFee && (
                                <small className="primary-color mb-4">Save ${item.saveOnMonthlyFee} annually</small>
                              )}
                            </li>
                            <li>
                              <p className="pt-4 mb-0">
                                Seat Fee * <b> ${item.employeeFee / monthNumber}</b>/
                                {translate('admin-signup.subscription.subscription-fees.mo')}
                              </p>
                              <small className="primary-color">
                                {translate('admin-signup.subscription.subscription-fees.seat-fee-hint')}
                              </small>
                            </li>
                          </ul>
                          <small>{translate('admin-signup.subscription.subscription-fees.annual-fee-reminder')}</small>
                        </div>
                      </Radio.Button>
                    ) : (
                      <Radio.Button className="white-large" value={item.code}>
                        <div style={{ height: '200px' }} className="pb-16">
                          <p className="radio-button-title">
                            {translate('admin-signup.subscription.subscription-fees.monthly')}
                          </p>
                          <ul className="pt-16">
                            <li>
                              <p className="mb-2 flex">
                                <div className="mr-4">Setup Fee</div>
                                {item.saveOnSetupFee > 0 && (
                                  <div className={'line-through text-decoration-red mr-4'}>
                                    <b> ${item.saveOnSetupFee} </b>
                                  </div>
                                )}

                                <div>
                                  <b>${item.setupFee}</b>
                                </div>
                              </p>
                              {item.saveOnSetupFee > 0 && (
                                <small className="primary-color mb-4">Save ${item.saveOnSetupFee}</small>
                              )}
                            </li>
                            <li>
                              <p className="mb-2 flex">
                                <div className="mr-4">Platform Fee</div>
                                {item.saveOnMonthlyFee > 0 && (
                                  <div className={'line-through text-decoration-red mr-4'}>
                                    <b> ${item.saveOnMonthlyFee}</b>/ mo
                                  </div>
                                )}
                                <div>
                                  <b> ${item.monthlyFee}</b>/ mo
                                </div>
                              </p>
                              {item.saveOnMonthlyFee > 0 && (
                                <small className="primary-color mb-4">Save ${item.saveOnMonthlyFee}/month</small>
                              )}
                            </li>
                            <li>
                              Seat Fee * <b> ${item.employeeFee}</b>/
                              {translate('admin-signup.subscription.subscription-fees.mo')}
                            </li>
                          </ul>
                        </div>
                      </Radio.Button>
                    )}
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </Col>
        )}
      </Row>

      {fees && (
        <>
          <Skeleton active loading={isLoadingFees}>
            <div className={'mb-8'}>
              {discount && discount.pageDescription && <DiscountAlert description={discount.pageDescription} />}
            </div>

            <Row justify="center">
              <Col span={24}>
                <div className="card">
                  <div className="inner-padding">
                    <h4>{translate('admin-signup.subscription.subscription-fees.todays-charge-label')}</h4>
                  </div>

                  <div className="border-top-bottom inner-padding">
                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={12}>{translate('admin-signup.subscription.subscription-fees.one-time-setup-fee')}</Col>
                      <Col span={12} className={'text-right'}>
                        {formatMoney(fees.setupFee)}
                      </Col>
                    </Row>

                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={18}>
                        <span>
                          {fees.frequency === SubscriptionFrequency.Annual
                            ? translate('admin-signup.subscription.subscription-fees.annual-platform-fee-label')
                            : translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-label')}
                        </span>

                        {fees.frequency === SubscriptionFrequency.Annual && (
                          <label className="annual-platform-fee">
                            (
                            {subscriptionPlanFees.filter((x) => x.frequency.id === SubscriptionFrequency.Annual)[0]
                              .monthlyFee / 12}{' '}
                            x 12 months)
                          </label>
                        )}

                        {fees.frequency === SubscriptionFrequency.Annual && (
                          <div className="annual-platform-fee-hint mt-8">
                            {translate('admin-signup.subscription.subscription-fees.annual-platform-fee-hint')}
                          </div>
                        )}
                      </Col>

                      <Col span={6} className={'text-right'}>
                        {formatMoney(
                          fees.frequency === SubscriptionFrequency.Monthly ? fees.proratedMonthlyFee : fees.monthlyFee,
                        )}
                      </Col>
                    </Row>

                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={18}>
                        {selectedSubscriptionPlan.frequency.id === SubscriptionFrequency.Annual ? (
                          <>
                            <span>{translate('admin-signup.subscription.minimum-seat')}</span>
                            <span className="gray-6">{`( $${fees.employeeFee / 12} x ${
                              fees.seatNumber
                            } seats) x 12 months`}</span>

                            {product.productId === ProductIds.Perks ? (
                              <div className="annual-platform-fee-hint mt-8">
                                {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-hint')}
                              </div>
                            ) : product.productId === ProductIds.QsehraId ? (
                              <div className="annual-platform-fee-qshera-hint mt-8">
                                {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-qshera-hint')}
                              </div>
                            ) : (
                              <div></div>
                            )}
                          </>
                        ) : (
                          <>
                            <span>{translate('admin-signup.subscription.minimum-seat')}</span>
                            <span className="gray-6">{`$${fees.employeeFee} x ${fees.seatNumber} seats`}</span>
                          </>
                        )}
                      </Col>

                      <Col span={6} className={'text-right'}>
                        <div>
                          {discount && discount.percent && discount.percent > 0 ? (
                            <>
                              <span className={'display-inline line-through text-decoration-red mr-4'}>
                                {formatMoney(
                                  fees.frequency == SubscriptionFrequency.Monthly
                                    ? fees.seatNumber * fees.proratedEmployeeFee
                                    : fees.seatNumber * fees.employeeFee,
                                )}
                              </span>

                              <span className={'display-inline'}>
                                {formatMoney(
                                  calculatePercentageDiscountedPrice(
                                    fees.frequency == SubscriptionFrequency.Monthly
                                      ? fees.seatNumber * fees.proratedEmployeeFee
                                      : fees.seatNumber * fees.employeeFee,
                                    discount.percent,
                                  ),
                                )}
                              </span>
                            </>
                          ) : (
                            <span className={'display-inline'}>
                              {formatMoney(
                                fees.frequency == SubscriptionFrequency.Monthly
                                  ? fees.seatNumber * fees.proratedEmployeeFee
                                  : fees.seatNumber * fees.employeeFee,
                              )}
                            </span>
                          )}
                        </div>

                        {fees.frequency === SubscriptionFrequency.Monthly && (
                          <div className="monthly-platform-fee-hint">
                            {translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-hint')}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="inner-padding">
                    <h4 className="inline ">{translate('admin-signup.subscription.subscription-fees.total-label')}</h4>

                    <span className="weight-600 float-right">
                      {formatMoney(
                        discount && discount.percent && discount.percent > 0
                          ? calculatePercentageDiscountedPrice(
                              fees.frequency == SubscriptionFrequency.Monthly
                                ? fees.seatNumber * fees.proratedEmployeeFee
                                : fees.seatNumber * fees.employeeFee,
                              discount.percent,
                            )
                          : fees.frequency == SubscriptionFrequency.Monthly
                          ? fees.seatNumber * fees.proratedEmployeeFee
                          : fees.seatNumber * fees.employeeFee,
                      )}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Skeleton>
        </>
      )}
    </>
  )
}

export default SubscriptionTypes
